import type { TRANSITION_CLASSNAMES } from '@oneToOne/shared/components/chat/constants'
import type { CSSRules, Directive } from 'twind/css'
import { css, keyframes } from 'twind/css'

export const TRANSITION_DURATION = '0.4s'
export const TRANSITION_TIMING_FUNCTION = 'cubic-bezier(0.4, 0, 0.2, 1)'

export const Transition = {
  transition: `all ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION}`,
}

export const FlexCenter = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

export const OTO_CSS = css`
  /* Colors */
  --primary-light: #e7f0ff;

  /* Ant Design Colors */
  --gray-1: #ffffff;
  --gray-2: #fafafa;
  --gray-3: #f5f5f5;
  --gray-4: #f0f0f0;
  --gray-5: #d9d9d9;
  --gray-6: #bfbfbf;
  --gray-7: #8c8c8c;
  --gray-8: #595959;
  --gray-9: #434343;
  --gray-10: #262626;
  --red-4: #ff7875;
  --red-5: #ff4d4f;

  /**
   * Experimental Dark mode.
   * To be replaced with the existing gray colors.
   */
  --cool-gray-50: #f4f7fa;
  --cool-gray-100: #e4e7eb;
  --cool-gray-200: #cbd2d9;
  --cool-gray-300: #9aa5b1;
  --cool-gray-400: #7b8794;
  --cool-gray-500: #616e7c;
  --cool-gray-600: #52606d;
  --cool-gray-700: #3e4c59;
  --cool-gray-800: #323f4b;
  --cool-gray-900: #1f2933;
  --cool-gray-1000: #1b242d;

  /* One-to-one Custom Colors */
  --one-to-one-green: #46995d;
  --one-to-one-approval-green: #2cc056;
  --one-to-one-red: #f96474;
  --one-to-one-orange-foreground: #f5a623;
  --one-to-one-orange-background: #fff7e6;
  --one-to-one-dark-blue: #353f52;

  /* Colors */
  --frosted-glass-dark: rgb(0 0 0 / 80%);
  --frosted-glass-light: rgb(255 255 255 / 80%);
  --frosted-glass-light-opaque: rgb(255 255 255 / 95%);
  --frosted-glass-button: rgb(64 64 64 / 50%);

  /* Spacing Scale */
  --spacing-2: 2px;
  --spacing-4: 4px;
  --spacing-6: 6px;
  --spacing-8: 8px;
  --spacing-12: 12px;
  --spacing-16: 16px;
  --spacing-18: 18px;
  --spacing-24: 24px;
  --spacing-32: 32px;
  --spacing-40: 40px;
  --spacing-48: 48px;

  /* Font Sizes */
  --font-size-xxs: 10px;
  --font-size-xs: 12px;
  --font-size-sm: 14px;
  --font-size-base: 16px;
  --font-size-md: 18px;
  --font-size-lg: 20px;
  --font-size-xl: 24px;
  --font-size-2xl: 30px;
  --font-size-3xl: 38px;
  --font-size-4xl: 46px;
  --font-size-5xl: 56px;

  /* Line Heights */
  --line-height-xs: 20px;
  --line-height-sm: 22px;
  --line-height-base: 24px;
  --line-height-lg: 28px;
  --line-height-xl: 30px;
  --line-height-2xl: 38px;
  --line-height-3xl: 46px;
  --line-height-4xl: 54px;
  --line-height-5xl: 64px;

  /* Transitions */
  --transition-all-medium: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  /* Font Weights */
  --font-normal: 400;
  --font-medium: 500;
  --font-semibold: 600;
  --font-bold: 700;

  /* Border Radius */
  --border-radius-sm: 4px;
  --border-radius-md: 8px;

  /* [OTO-943]: Respect user motion preferences */
  @media (prefers-reduced-motion: reduce) {
    * {
      opacity: 1 !important;
      transition: none !important;
      animation: none !important;
    }
  }
`

const fadeIn = keyframes({
  '0%': {
    opacity: 0,
  },
  '40%': {
    opacity: 0,
  },
  '100%': {
    opacity: 1,
  },
})

// When there are are transitions to wait for, this animation is preferred
// as it will allow the transition to happen before the content is faded In
// which prevents jarring / resizing visual gore
export const FadeInAnimation = {
  animationName: fadeIn,
  animationDuration: '0.7s',
  animationTimingFunction: TRANSITION_TIMING_FUNCTION,
  animationFillMode: 'forwards',
  opacity: 0,
}

const fadeInNoDelay = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
})

// Base Fade In Animation
const BaseFadeInAnimation = {
  animationName: fadeInNoDelay,
  animationTimingFunction: TRANSITION_TIMING_FUNCTION,
  animationFillMode: 'forwards',
}

// When there are no transitions to wait for this animation is preferred
// over FadeInAnimation, as there is no need for the delay
export const FadeInNoDelayAnimationFast = {
  ...BaseFadeInAnimation,
  animationDuration: '0.3s',
}

type ValueOf<T> = T[keyof T]

interface generateTransitionGroupStylesParams {
  className: ValueOf<typeof TRANSITION_CLASSNAMES>
  startStyle: Directive<CSSRules>
  endStyle: Directive<CSSRules>
}

export const generateTransitionGroupStyles = ({
  className,
  endStyle,
  startStyle,
}: generateTransitionGroupStylesParams) => {
  return css`
    .${className}-enter {
      ${startStyle}
    }

    .${className}-enter-active {
      transition: all ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};

      ${endStyle}
    }

    .${className}-exit {
      ${endStyle}
    }

    .${className}-exit-active {
      transition: all ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};

      ${startStyle}
    }
  `
}

export const actionButton = ({
  actionType,
  background,
  buttonSize,
  shape,
}: {
  actionType?: 'danger' | 'neutral' | 'success' | 'subtle'
  background?: string
  buttonSize: 'xs' | 'sm' | 'md' | 'lg'
  shape: 'circle' | 'square'
}) => css`
  ${Transition}
  padding: unset;
  aspect-ratio: 1 / 1;
  border-radius: ${shape === 'circle' ? '50%' : 'var(--border-radius-sm)'};
  background: ${
    actionType === 'danger'
      ? 'var(--one-to-one-red)'
      : actionType === 'success'
        ? 'var(--one-to-one-green)'
        : actionType === 'neutral'
          ? 'var(--frosted-glass-button)'
          : background
            ? background
            : 'inherit'
  } !important;

  height: ${
    buttonSize === 'xs'
      ? '22px'
      : buttonSize === 'sm'
        ? '30px'
        : buttonSize === 'md'
          ? '40px'
          : '50px'
  };

  width: ${
    buttonSize === 'xs'
      ? '22px'
      : buttonSize === 'sm'
        ? '30px'
        : buttonSize === 'md'
          ? '40px'
          : '50px'
  };

  &:hover {
    filter: brightness(0.94);
  }
`
